import { globalSettings, developmentSettings} from './plan-settings.js';
import Landscape from './plan-landscape.js';
import { Plan, PlanProduct } from './plan.js';

// Core (via pnpm)
import * as THREE from 'three';

// Addons (via pnpm)
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'; // Load skybox images (envmap)
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js' // Only needed if a compressed GLB file is provided
import { Sky } from 'three/addons/objects/Sky.js';

// import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
// import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
// import { OutlinePass } from 'three/addons/postprocessing/OutlinePass.js';
// import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
// import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

// import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
// import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';

// import { HDRCubeTextureLoader } from 'three/addons/loaders/HDRCubeTextureLoader.js';
// import { Reflector } from 'three/addons/objects/Reflector.js';

// import { LensFlare } from "@andersonmancini/lens-flare";
// import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
// import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader";
// import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
// import { Wireframe } from 'three/addons/lines/Wireframe.js';
// import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js';


// Dev
import Stats from 'three/addons/libs/stats.module.js';
import Debug from './plan-debug.js';
// import { GUI } from 'three/addons/libs/lil-gui.module.min.js';

const query = window.location.search.replace('?', '').split('&');

const DEV = !!query.find(param => param.indexOf('dev') !== -1);
const LANDSCAPE = !query.find(param => param.indexOf('noland') !== -1);
const HIGHLIGHT_AVAILABLES = false;
const SHOW_AVAILABLES_ONLY = !!query.find(param => param.indexOf('sinnavidad') !== -1);
const HITBOX = !query.find(param => param.indexOf('nohitbox') !== -1);
const ENV_HDR = 1;
const BG_HDR = 1;
const ORTO = 0;

class View{
	constructor(data){
		this.data = data;
		this.threshold = .0001;

		/*
		for (k in args){
			this.data[k] = args[k];
		}
		*/
	}

	isDifferentFrom(view){
		for (const k in this.data){
			if (typeof this.data[k] === 'object'){
				// Nested objects
				for (const n in this.data[k]){
					if (Math.abs(this.data[k][n] - view.data[k][n]) > this.threshold){
						// console.log( k, n, this.data[k][n], view.data[k][n] );
						return true;
					}
				}

			}
			else{
				if (Math.abs(this.data[k] - view.data[k]) > this.threshold){
					// console.log( k, this.data[k], view.data[k] );
					return true;
				}
			}
		}
	
		return false;
	}

	merge(target, source){
		for (const key of Object.keys(source)) {
			if (source[key] instanceof Object) Object.assign(source[key], this.merge(target[key], source[key]))
		}

		Object.assign(target || {}, source)
		
		return target
	}

	copy(){
		return new View(JSON.parse(JSON.stringify(this.data)));
	}

	set(data){
		this.data = this.merge(this.data, data);
	}
}

class Skybox{
	constructor(renderer, settings){
		this.renderer = renderer;
		this.object = new Sky();
		this.object.scale.setScalar(1000); // Controls skybox size (and sun too) - in exemple: 450000
		this.sun = new THREE.Vector3();

		this.settings = {
			turbidity: 10,
			rayleigh: 1, // initial: 3
			mieCoefficient: .005,
			mieDirectionalG: .5, // initial: .7
			elevation: 2,
			azimuth: 180,
			// exposure: .5, // looks better like in example (.5) but makes model too dark
			exposure: .7, // looks better like in example (.5) but makes model too dark
			...settings
		}

		// Never change, move it to update() to update them alogn with sun position
		this.object.material.uniforms[ 'turbidity' ].value = this.settings.turbidity;
		this.object.material.uniforms[ 'rayleigh' ].value = this.settings.rayleigh;
		this.object.material.uniforms[ 'mieCoefficient' ].value = this.settings.mieCoefficient;
		this.object.material.uniforms[ 'mieDirectionalG' ].value = this.settings.mieDirectionalG;
		// this.renderer.toneMappingExposure = this.settings.exposure;
		
		this.update();
	}

	set(key, value){
		this.settings[key] = value;
	}

	update(settings = {}){
		if (!Object.values(settings).length){ // Allow GUI to pass settings
			settings = this.settings;
		}

		// Sun position
		const phi = THREE.MathUtils.degToRad(90 - settings.elevation);
		const theta = THREE.MathUtils.degToRad( settings.azimuth );
		this.sun.setFromSphericalCoords( 1, phi, theta );

		// Other settings
		this.object.material.uniforms[ 'sunPosition' ].value.copy(this.sun);
		// this.object.geometry.boundingSphere.radius = .3
		// Never change, moved to contructor
		// this.object.material.uniforms[ 'turbidity' ].value = settings.turbidity;
		// this.object.material.uniforms[ 'rayleigh' ].value = settings.rayleigh;
		// this.object.material.uniforms[ 'mieCoefficient' ].value = settings.mieCoefficient;
		// this.object.material.uniforms[ 'mieDirectionalG' ].value = settings.mieDirectionalG;
		
		this.renderer.toneMappingExposure = settings.exposure;

		window.dispatchEvent(new CustomEvent('cmzPlanSkyboxChanged', {detail: this.object}));
	}
}

class Plan3D_Product{
	constructor(object, plan){
		this.id = object.name;
		this.plan = plan;
		this.object = object;
		
		this.planProduct;  // Set by PlanProduct
		
		// this.materials = {};

		// Create clone
		// let geometries = [];

		// console.log(object.name);
		this.forEachMesh((mesh, nested) => {
			// Backup initial material
			if (mesh?.material){
				mesh.originalMaterial = mesh.material.clone();
			}
			
			// Isolate for raycasting efficiency
			if (!nested){
				mesh.layers.enable(1);
			}

			if (HITBOX){
				mesh.visible = false;
				mesh.castShadow = false;

				if (mesh?.material){
					// mesh.originalMaterial.visible = false;
					
					// console.log(mesh.material);
					mesh.material = new THREE.MeshStandardMaterial({
						color: 0xffffff,
						// visible: false,
						transparent: true,
						// opacity: 0,
					});	
				}
			}

			return;

			// Remove shadows
			// mesh.castShadow = false;
			// mesh.receiveShadow = false;
			
			// mesh.material.depthWrite = false;

			// Move mesh below outline
			mesh.renderOrder = 0;

			// Backup materials
			// this.materials[mesh.uuid] = mesh.material;

			// Collect geometry to create fill and outline
			const geometry = mesh.geometry.clone();
			delete geometry.attributes.uv; // remove uv so that all meshes have same attributes (mergeGeometries can't parse it otherwise)
			geometries.push(geometry);

		});

		// const geometry = BufferGeometryUtils.mergeGeometries(geometries, false);
		// geometry.translate(object.position.x, object.position.y, object.position.z);


		// this.fill = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({visible: false}));
		// this.stroke = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), new THREE.LineBasicMaterial({visible: false}));

		// this.fill.castShadow = false;
		// this.fill.receiveShadow = false;
		// this.stroke.castShadow = false;
		// this.stroke.receiveShadow = false;

		// this.object.add(this.fill);
		// this.object.add(this.stroke);
	}

	forEachMesh(callback){
		// Try first on object itself in case it's not a group of meshes
		if (this.object.isMesh){
			callback(this.object, false);
			return;
		}

		this.object.traverse(child => {
			if (child.isMesh){
				callback(child, child.parent.uuid != this.object.uuid);
			}
		});
	}
	/*
	onStateChange(){
		// Update appareance (child function)
		this.updateAppearance();
		
		// if (render){
			this.plan.render();
		// }
	}*/

	updateState(status, states){ // Fire by PlanProduct
		let style = {};

		// Blocked
		if (status == 'hidden'){
			style.visible = !SHOW_AVAILABLES_ONLY;
			
			if (states.hover){
				style.visible = true;
				style.emissiveIntensity = HITBOX ? 1.5 : 1.25;
				
				if (SHOW_AVAILABLES_ONLY){
					style.opacity = .4;
				}
			}
			
			this.setMaterial('hidden', style);
		}
		else if (!states.available){ // Unavailable (booked, prebooked, etc. - all except hidden)
			style.visible = !SHOW_AVAILABLES_ONLY;

			// if (SHOW_AVAILABLES_ONLY){
			// 	style.opacity = 0;
			// }

			if (states.hover){
				style.emissiveIntensity = HITBOX ? 1.5 : 1.25;
				style.visible = true;
				
				if (SHOW_AVAILABLES_ONLY){
					style.opacity = .5;
				}
			}

			this.setMaterial('unavailable', style);
			/*
			this.forEachMesh(mesh => {
				if (!mesh.material) return;

				if (HITBOX){
					// mesh.material = mesh.originalMaterial.clone();
					
					// mesh.material.transparent = true;
					// mesh.material.visible = true;
					// mesh.material.color.set(0xa02626); // #a02626
					// mesh.material.emissive.setHex( 0xff0000 ); // #ff0000
					// mesh.material.emissiveIntensity = .5;
					// mesh.material.opacity = .4;

					// if (states.hover){
					// 	mesh.material.emissiveIntensity = 1.5;
					// }
				}
				else{
					// const mat = mesh.originalMaterial.clone();
					
					// if (STYLE_1){
					// 	mat.emissive.setHex(0xff0000); // #ff0000
					// 	mat.emissiveIntensity = .75;
					// }
					// else{
					// 	mat.color.set(0xc02424); // #c02424
					// 	mat.emissive.setHex(0xc02424); // #c02424
					// 	mat.emissiveIntensity = .75;
					// 	mat.map = null;
					// 	mat.metalnessMap = null;
					// 	mat.normalMap = null;
					// 	mat.roughnessMap = null;
					// 	mat.metalness = 1;
					// 	mat.roughness = .15;
					// 	mat.transparent = true;
					// 	mat.opacity = .85;
					// }
					
					// if (states.hover){
					// 	mat.emissiveIntensity = 1.25;
					// }
					
					// mesh.material = mat;
				}
			});*/
		}
		else if (states.filtered){
			style.visible = !SHOW_AVAILABLES_ONLY;

			// if (SHOW_AVAILABLES_ONLY){
			// 	style.opacity = 0;
			// }

			if (states.hover){
				style.emissiveIntensity = HITBOX ? 1.25 : 1;
				style.visible = true;
				
				if (SHOW_AVAILABLES_ONLY){
					style.opacity = .6;
				}
			}

			this.setMaterial('filtered', style);
		}/*
		else if (HIGHLIGHT_AVAILABLES){ // Available (if enabled)
			style.visible = true;

			if (states.hover){
				// style.emissiveIntensity = 1.25;
				style.opacity = .6;
			}
					
			if (states.open || states.focus){
				// style.emissiveIntensity = 1.5;
				// style.opacity = states.hover ? .8 : .7;	
				style.opacity = states.hover ? .8 : .7;	
			}

			this.setMaterial('available', style);
		}*//*
		else if (states.hover || states.open || states.focus){ // Available (if interaction)
			if (states.hover){
				if (HITBOX){
					style.opacity = .65;
				}
				else{
					mat.emissiveIntensity = .25;
				}
			}

			// Available open/focus
			if (states.open || states.focus){
				if (HITBOX){
					style.opacity = states.hover ? .8 : .7;	
				}
				else{
					mat.emissiveIntensity = states.hover ? .8 : .6;	
				}
			}

			style.visible = true;
			this.setMaterial('highlighted', style);
		}*/
		else{ // Available (if idle)
			style.visible = this.plan.settings.availableVisible || SHOW_AVAILABLES_ONLY;

			if (states.hover){
				style.visible = true;
				
				if (HITBOX){
					style.opacity = .65;
				}
				else{
					style.emissiveIntensity = .25;
				}
			}

			// Available open/focus
			if (states.open || states.focus){
				style.visible = true;

				if (HITBOX){
					style.opacity = states.hover ? .8 : .7;	
				}
				else{
					style.emissiveIntensity = states.hover ? .8 : .6;	
				}
			}

			this.setMaterial('available', style);
		}

		// Render after style change
		// this.render();
		
		// Update cursor
		this.plan.planView.style.setProperty('cursor', (this.planProduct && this.planProduct.states.available ? 'pointer' : 'auto'))

		// Render
		this.plan.render();
	}

	setMaterial(state, custom = {}){
		// console.log(state);

		const stateMaterial = this.plan.settings.materials.hasOwnProperty(state) ? this.plan.settings.materials[state] : {}; 
		const material = {...stateMaterial, ...custom};
		
		this.forEachMesh(mesh => {
			if (!mesh.material) return;
			
			// Start from original
			mesh.material = mesh.originalMaterial.clone();
			
			// Overwrite with state color
			for (const k in material){
				switch (k){
					case 'color':
						mesh.material.color.set(parseInt(material[k].replace("#", "0x"), 16));
					break;
					
					case 'emissive':
						mesh.material.emissive.setHex(parseInt(material[k].replace("#", "0x"), 16));
					break;
					
					case 'visible': // Visibility is applide to mesh, not material (prevent issue with transparency)
						mesh.visible = material[k];
					break;

					default:
						mesh.material[k] = material[k];
					break;
				}
			}
		});
	}
}

export default class Plan3D{
	constructor(spotNode, displaySettings, planService){
		// super();
		this.file = CMZ.selector.plan3D;

		if (!this.file){
			console.warn('No plan file found');
			return;
		}
		
		this.slug = this.file.split('/');
		this.slug = this.slug[this.slug.length-1].replace('.glb', '');

		this.spot = spotNode;
		this.planService = planService;
		this.planView = this.spot.querySelector('.cmz-spot-view-plan3d');
		this.canvas = this.spot.querySelector('.cmz-spot-plan-canvas');
		this.loader = this.spot.querySelector('.cmz-spot-plan-loader');
		this.renderTimeout;

		this.products = [];
		// this.productHovered = null;
		// this.clock = new THREE.Clock();
		this.running = false;

		// this.interactions = { // rename highlights, or needed for somthing else?
		// 	focus: null,
		// 	open: null,
			hover: null,
		// }

		this.productHovered = undefined;

		this.update = {
			landscape: false,
			compass: true,
		};
		
		const {width, height} = this.getSize();
		// const selectedObjects = [];

		this.cursor = {
			previous: new THREE.Vector2(),
			current: new THREE.Vector2(),
		};

		this.settings = globalSettings;

		// Helper for merging object recusively
		function mergeSettings(base, overwrite) {
			for (const k in overwrite){
				const value = overwrite[k];
				
				// Nested object
				if (typeof value === 'object' && !Array.isArray(value) && value !== null){
					// Create key if doesn't exist
					if (!base[k] || typeof base[k] !== 'object') {
						base[k] = {};
					}

					base[k] = mergeSettings(base[k], value);
				}
				else{
					base[k] = value;
				}
			}

			return base;
		}

		// Overwrite settings with property specific ones
		if (this.file){
			// Load settings
			if (developmentSettings.hasOwnProperty(this.slug)){
				console.log( 'Settings loaded for:', this.slug, developmentSettings[this.slug] );
				this.settings = mergeSettings(this.settings, developmentSettings[this.slug]);

			}
		}

		// Set settings from formData
		// ...

		// Convert settings from easy human read to threejs
		this.settings.defaultView.angle.x = 180 - this.settings.defaultView.angle.x;
		// this.settings.defaultView.angle.x = 180 - this.settings.defaultView.angle.x;
		this.settings.defaultView.angle.y = 90 - this.settings.defaultView.angle.y;
		
		const minPolarAngle = this.settings.minPolarAngle;
		const maxPolarAngle = this.settings.maxPolarAngle;
		this.settings.minPolarAngle = 90 - maxPolarAngle;
		this.settings.maxPolarAngle = 90 - minPolarAngle;

		// Render
		this.renderer = new THREE.WebGLRenderer({antialias: true, canvas: this.canvas/*, logarithmicDepthBuffer: true*/});
		this.renderer.setSize(width, height);
		this.renderer.shadowMap.enabled = true;
		this.renderer.localClippingEnabled = true; // Allow landscape to be clipped
		// this.renderer.toneMapping = THREE.ACESFilmicToneMapping; // Added for Skybox exposure		

		// Scene
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color(0xe8e8ed);

		// Camera
		if (ORTO){
			this.camera = new THREE.OrthographicCamera(width / -2, width / 2, height / 2, height / -2, 1, 2000);
			this.camera.zoom = this.settings.defaultView.zoom;
			// this.camera.position.set( this.settings.defaultView.x, this.settings.defaultView.y, this.settings.defaultView.z );
			this.camera.position.set(0, 0, -1); // North
		}
		else{
			this.camera = new THREE.PerspectiveCamera(45, width / height, this.settings.camera.near, this.settings.camera.far); // distance
			// this.camera.position.set(this.settings.defaultView.x, this.settings.defaultView.y, this.settings.defaultView.z);
			this.camera.position.set(0, 1, 1); // North
			// this.camera.zoom = 1;
			// this.camera.fov = 40;
		}

		this.camera.updateProjectionMatrix();

		// Controls
		this.controls = new OrbitControls(this.camera, this.renderer.domElement);

		// Set initial angles
		this.controls.minPolarAngle = THREE.MathUtils.degToRad(this.settings.defaultView.angle.y);
		this.controls.maxPolarAngle = THREE.MathUtils.degToRad(this.settings.defaultView.angle.y);
		this.controls.minAzimuthAngle = THREE.MathUtils.degToRad(this.settings.defaultView.angle.x);
		this.controls.maxAzimuthAngle = THREE.MathUtils.degToRad(this.settings.defaultView.angle.x);
		this.controls.update();

		// Reset min/max angles
		this.controls.minPolarAngle = THREE.MathUtils.degToRad(this.settings.minPolarAngle);
		this.controls.maxPolarAngle = THREE.MathUtils.degToRad(this.settings.maxPolarAngle);
		this.controls.minAzimuthAngle = -Infinity;
		this.controls.maxAzimuthAngle = Infinity;

		this.controls.enablePan = this.settings.pan;
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;

		/**
		 * Default
		 * ONE: ROTATE
		 * TWO: DOLLY_PAN
		 * 
		 * ROTATE: 0 (orbit)
		 * PAN: 1 (pan)
		 * DOLLY_PAN: 2 (zoom)
		 * DOLLY_ROTATE: 3 (zoom + orbit)
		 */
		if (this.settings.pan){
			this.controls.touches = {
				ONE: THREE.TOUCH.PAN,
				TWO: THREE.TOUCH.DOLLY_ROTATE,
			};
		}
		
		if (ORTO){
			this.controls.minZoom = this.settings.minZoom;
			this.controls.maxZoom = this.settings.maxZoom;
		}
		else{
			this.controls.minDistance = this.settings.defaultView.distance;
			this.controls.maxDistance = this.settings.defaultView.distance;
			this.controls.update();

			this.controls.minDistance = this.settings.minDistance;
			this.controls.maxDistance = this.settings.maxDistance;
		}

		const lightSettings = this.settings.lights[displaySettings?.light || this.settings.defaultView.time];

		// Env (legacy)
		// const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
		// this.scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;

		// Env (skybox)
		this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
		this.sceneEnv = new THREE.Scene();
		this.renderTarget;

		if (!ENV_HDR || !BG_HDR){
			window.addEventListener('cmzPlanSkyboxChanged', (e) => {
				// Update env light when skybox changes (sun is moved)
				if ( this.renderTarget !== undefined ) this.renderTarget.dispose();
				
				const sky = e.detail;
				
				if (!ENV_HDR){
					this.sceneEnv.add(sky);
					this.renderTarget = this.pmremGenerator.fromScene(this.sceneEnv);
					this.scene.environment = this.renderTarget.texture;
				}

				if (!BG_HDR){
					this.scene.add(sky);
				}
			});
		}

		// Skybox
		this.sky = new Skybox(this.renderer, {
			elevation: lightSettings.sky.y,
			azimuth: lightSettings.sky.x,
			exposure: lightSettings.sky.exposure,
		});

		// this.scene.add(this.sky.object);


		// Fog
		this.scene.fog = new THREE.Fog(lightSettings.fog, this.settings.camera.far * .4, this.settings.camera.far * .9);

		// syncFogWithSky(this.renderer, this.sky.object, this.scene.fog);

		// Lights
		// this.scene.add(new THREE.AmbientLight(lightSettings.color, 3)); // @perf - 5fps
		// this.scene.add(new THREE.AmbientLight(lightSettings.color, 6)); // @perf - 5fps

		this.sun = new THREE.DirectionalLight(lightSettings.color, lightSettings.intensity);
		this.sun.name = 'cmz-sun';
		this.sun.position.set(lightSettings.position.x, lightSettings.position.y, lightSettings.position.z);
		this.sun.color.set(this.settings.sunLight.color);

		const d = 100; // Size of the shadow camera box. Everything outside won't cast shadow, but the bigest, the less quality it gets so it should fit as close as possible to the model size.

		this.sun.castShadow = true;
		this.sun.shadow.camera.left = -d;
		this.sun.shadow.camera.right = d;
		this.sun.shadow.camera.top = d;
		this.sun.shadow.camera.bottom = -d;
		this.sun.shadow.mapSize.set(this.settings.shadowQuality, this.settings.shadowQuality);
		// this.sun.shadow.bias = -0.005; // Remove weird stripes on materials
		this.sun.shadow.bias = -0.000275; // Remove weird stripes on materials (must be as low as possible because it also offset shadows)
		this.sun.shadow.normalBias = 0.01; // Remove shadow displacement
		
		// this.sun.shadow.intensity = 2;
		// this.sun.shadow.camera.rotation.x = 10;
		// this.sun.shadow.camera.far = d * 2; // Default 500

		// this.sun.rotation.x = 100;
		
		this.scene.add(this.sun);
		
		// Spotlight
		// this.spotlight = new THREE.SpotLight(lightSettings.color, 10 * 10000 );
		// this.spotlight.name = 'cmz-spotlight';
		// this.spotlight.angle = Math.PI / 5;
		// this.spotlight.penumbra = 0.3;
		// this.spotlight.position.set(lightSettings.position.x, lightSettings.position.y, lightSettings.position.z);
		// this.spotlight.castShadow = true;
		// this.spotlight.shadow.camera.near = 8;
		// this.spotlight.shadow.camera.far = 300;
		// this.spotlight.shadow.mapSize.height = this.settings.shadowQuality;
		// this.spotlight.shadow.mapSize.width = this.settings.shadowQuality;

		// this.scene.add(this.spotlight);

		// Hemisphere Light
		this.hemisphere = new THREE.HemisphereLight( this.settings.hemisphereLight.color.color, this.settings.hemisphereLight.color.groundColor, this.settings.hemisphereLight.intensity );
		this.hemisphere.name = 'cmz-hemisphere';
		this.hemisphere.position.set( 0, 50, 0 );

		this.scene.add(this.hemisphere);
		/*
		const product1A = new THREE.Mesh(new THREE.BoxGeometry(6, 8, 13), new THREE.MeshStandardMaterial({
			color: 0xffffff,
			metalness: 1,
			roughness: 0,
		}));

		product1A.name = 'product-1A';
		product1A.position.set(-3, 4, -2.5);
		this.scene.add(product1A);

		const product1B = new THREE.Mesh(new THREE.BoxGeometry(6, 8, 13), new THREE.MeshStandardMaterial({
			color: 0xffffff,
			metalness: 1,
			roughness: 0,
		}));

		product1B.name = 'product-1B';
		product1B.position.set(3, 4, -2.5);
		this.scene.add(product1B);

		const reflector = new Reflector( new THREE.BoxGeometry(10, 10, 10), {
			clipBias: 0.05,
			// clipBias: 0.003,
			textureWidth: window.innerWidth * window.devicePixelRatio,
			textureHeight: window.innerHeight * window.devicePixelRatio,
			color: 0xc1cbcb
		} );

		reflector.position.y = 10;
		reflector.position.x = 30;

		// this.scene.add(reflector);
		*/
		// Ground
		/*const ground = new THREE.Mesh(new THREE.BoxGeometry(5000, 0.1, 5000), new THREE.MeshPhongMaterial({color: new THREE.Color(0xFFFFFF)}));
		ground.position.set(0, -.15, .15);
		ground.name = 'cmz-ground';
		ground.receiveShadow = true;
		this.scene.add(ground);*/

		// Raycaster
		this.raycaster = new THREE.Raycaster();
		this.raycaster.layers.set(1);

		// Dev
		if (DEV){
			// Lights helpers
			// this.scene.add(new THREE.CameraHelper(this.sun.shadow.camera));
			// this.scene.add(new THREE.CameraHelper(this.spotlight.shadow.camera));

			// Axis helpers
			this.scene.add(new THREE.AxesHelper(500));

			// Stats
			this.stats = new Stats();
			this.canvas.parentNode.appendChild(this.stats.dom);

			// Gui
			// const gui = new GUI();
			this.debug = new Debug(this);
		}

		// Shaders
		// https://github.com/OmarShehata/webgl-outlines/blob/main/threejs-outlines-minimal/src/index.js
		// https://medium.com/@coderfromnineteen/three-js-post-processing-outline-effect-6dff6a2fe3c0
		// https://threejs.org/examples/#webgl_shaders_sky

		// Postprocessing
		/*
		this.composer = new EffectComposer(this.renderer);

		const renderPass = new RenderPass(this.scene, this.camera);
		this.composer.addPass(renderPass);

		// const bloomPass = new UnrealBloomPass( new THREE.Vector2( window.innerWidth, window.innerHeight ), 1.5, 0.4, 0.85 );
				// bloomPass.threshold = .8;
				// bloomPass.strength = 1;
				// bloomPass.radius = .5;
		// this.composer.addPass(bloomPass);

		this.outlinePass = new OutlinePass(new THREE.Vector2(window.innerWidth/200, window.innerHeight/200), this.scene, this.camera);
		this.outlinePass.edgeStrength = 2;
		this.outlinePass.edgeGlow = 0;
		this.outlinePass.edgeThickness = 1;
		this.outlinePass.visibleEdgeColor.set(0xffffff);
		this.outlinePass.hiddenEdgeColor.set(0xffffff); // 0x787878

		// this.composer.addPass(this.outlinePass); // @perf -5 fps

		const outputPass = new OutputPass();
		this.composer.addPass(outputPass); // @perf -7 fps
		*/


		// @todo: test if outline a simple cube has better perf
		// const cube = new THREE.Mesh(new THREE.BoxGeometry(20,20,20), new THREE.MeshBasicMaterial({color: 0xcccccc}) );
		// cube.position.set( 0, 50, 0 );
		// cube.castShadow = true;
		// cube.receiveShadow = true;
		// this.scene.add( cube );
		// this.outlinePass.selectedObjects = [cube];

		// Create visible table
		/*
		const visibleProductIds = [];

		CMZ.products.forEach(product => {
			if (product.visible){
				visibleProductIds.push(product.id)
			}
		})
		*/

		// Model
		

		// View
		this.views = {
			initial: this.getCurrentView(), // Not accurate, light is resetting just after
			previous: this.getCurrentView(),
			current: this.getCurrentView(),
			target: this.getCurrentView(),
		};

		// Reset initial light (current view has light from user session instead of default)
		this.views.initial.set({
			sun: this.settings.lights[this.settings.defaultView.time].position,
			sky: {
				x: this.settings.lights[this.settings.defaultView.time].sky.x,
				y: this.settings.lights[this.settings.defaultView.time].sky.y,
			}
		});

		// Events
		window.addEventListener('resize', this.onWindowResize.bind(this));

		// Products
		this.renderer.domElement.style.touchAction = 'none';
		this.renderer.domElement.addEventListener('pointermove', this.onPointerMove.bind(this));
		
		// Update cursor position
		this.renderer.domElement.addEventListener('mousedown', e => {
			const {x, y} = this.getCursorPosition(e);
			
			this.cursor.previous.set(x, y);
			this.cursor.current.set(x, y);
		});

		// Highlight product and open popups
		this.renderer.domElement.addEventListener('mouseup', e => { 
			// Consider it's a click if cursor (almost) didn't move since mousedown
			if (this.cursor.current.distanceTo(this.cursor.previous) < 0.025){
				this.onClick(e);
			}
		});

		// Tools
		this.tools = {
			compass: this.spot.querySelector('.cmz-spot-plan3d-toolbar-compass'),
			rotateLeft: this.spot.querySelector('.cmz-spot-plan-toolbar-rotate-left'),
			rotateRight: this.spot.querySelector('.cmz-spot-plan-toolbar-rotate-right'),
			zoomIn: this.spot.querySelector('.cmz-spot-plan-toolbar-zoomin'),
			zoomOut: this.spot.querySelector('.cmz-spot-plan-toolbar-zoomout'),
		}

		// Light
		this.spot.querySelectorAll('input[name="cmz[spot][light]"]').forEach(input => {
			input.addEventListener('change', e => {
				this.setView({
					sun: this.settings.lights[e.target.value].position,
					sky: {
						x: this.settings.lights[e.target.value].sky.x,
						y: this.settings.lights[e.target.value].sky.y,
						exposure: this.settings.lights[e.target.value].sky.exposure,
					},
					hemisphere: {
						color: this.settings.lights[e.target.value].color
					}
				});

				// Dev
				// const {x, y, z} = this.settings.lights[e.target.value];
				// cube.position.set( x, y, z );
			});
		});

		// Rotate
		this.tools.rotateLeft.addEventListener('click', () => {
			this.setView({
				angle: {
					x: this.controls.getAzimuthalAngle() + THREE.MathUtils.degToRad(-90),
				},
			});
		});

		this.tools.rotateRight.addEventListener('click', () => {
			this.setView({
				angle: {
					x: this.controls.getAzimuthalAngle() + THREE.MathUtils.degToRad(90),
				}
			});
		});

		// Zoom
		this.tools.zoomIn.addEventListener('click', () => {
			this.setView({distance: Math.max(this.controls.getDistance() / this.settings.zoomFactor, this.settings.minDistance)})
			// this.setView({zoom: this.camera.zoom * this.settings.zoomFactor})
		});

		this.tools.zoomOut.addEventListener('click', () => {
			this.setView({distance: Math.min(this.controls.getDistance() * this.settings.zoomFactor, this.settings.maxDistance)})
			// this.setView({zoom: this.camera.zoom / this.settings.zoomFactor})
		});

		// Compass
		this.tools.compass.addEventListener('click', () => {
			this.setView({
				angle: this.views.initial.data.angle,
				target: this.views.initial.data.target,
				zoom: this.views.initial.data.zoom,
			});
		});

		// Remove highlight when spot product unselect
		this.spot.querySelector('#cmz-spot-plan-product-none').addEventListener('change', () => {
			// Unlock camera
			this.controls.enablePan = true;
			// this.setInteraction('open', null);
			this.render();
		})

		// Updates
		this.controls.addEventListener('start', () => {
			// console.log( 'start' );
			this.update.compass = true;
			this.start();
		});

		this.controls.addEventListener('end', () => {
			// console.log( 'end' );
			this.update.compass = false;

			// Manage autostop
			this.autostopIsOn = true;
			// this.stop(); // Not working with damping
		});

		// const minPan = new THREE.Vector3(-210,0,-80);
		// const maxPan = new THREE.Vector3(210,0,100);

		// const _v = new THREE.Vector3(); 

		// Limit camera pan (@todo miss implementation for Y axis. We would need to limit max height the camera can move when working w bulidings)
		this.controls.addEventListener('change',() => {

			// console.log( this.controls.target );

			// Do not limit camera if view is currently changing (for when user click rotate or compass buttons)
			if (this.update.view){
				return;
			}

			// Zoom in percentage from 0 to 1
			const zoomAbs = this.controls.maxDistance - this.controls.getDistance();
			const zoomRel = Math.max(0, Math.min(1, (this.controls.getDistance() - this.controls.minDistance) / (this.controls.maxDistance - this.controls.minDistance)));
			
			const dynamicBoundaries = 20 + zoomAbs; // Limit proportional to zoom level: Initial limit + zoom level > Bounding limits increase when zoom in
			const reducer = .5 + (.5 * zoomRel); // Get a reducer that varies from 50% to 100% so that the dynamicBoundaries is not linear. It increase faster as the scene is zoomed in.
			
			const max = dynamicBoundaries * reducer;
			const min = max * -1;
			
			// const minPan = new THREE.Vector3(min-panZoomFactor, 0, min-panZoomFactor);
			// const maxPan = new THREE.Vector3(max+panZoomFactor, 0, max+panZoomFactor);
			
			// const minPan = new THREE.Vector3(min, -Infinity, min);
			// const maxPan = new THREE.Vector3(max, Infinity, max);
			const minPan = new THREE.Vector3(min, 0, min);
			const maxPan = new THREE.Vector3(max, 0, max);

			const v = new THREE.Vector3();
		    v.copy(this.controls.target);
		    this.controls.target.clamp(minPan, maxPan);
		    v.sub(this.controls.target);
		    this.camera.position.sub(v);
		});

		// Autostart
		/*
		if (this.running){
			this.running = false;
			this.start();
		}
		*/
	}

	initialize(){
		if (this.initialized) return;

		this.initialized = true;

		// Tiles
		this.landscape = new Landscape();

		// Load model
		this.loadModel();
	}

	loadModel(){
		if (ENV_HDR || BG_HDR){
			const envmaps = {
				sky: 'kloofendal_48d_partly_cloudy_puresky_1k.hdr',
				sky: 'sky-2.hdr',
				sky: 'qwantani_dusk_1_puresky_1k.hdr',
			};

			new RGBELoader().load(window.location.origin + '/wp-content/plugins/customizer-evoost/assets/' + envmaps.sky, (texture) => {
				texture.mapping = THREE.EquirectangularReflectionMapping;
				
				if (ENV_HDR){
					this.scene.background = texture;
				}
				
				if (BG_HDR){
					this.scene.environment = texture;
					this.scene.environmentIntensity = this.settings.environmentIntensity;
				}

				this.render();
			});
		}

		const loader = new GLTFLoader();

		// Load compressed models
		const dracoLoader = new DRACOLoader();
		dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
		dracoLoader.setDecoderConfig({ type: 'js' });
		loader.setDRACOLoader(dracoLoader);

		// this.camera.layers.enable(1);
		// this.camera.layers.enable(2);
		
		// Load model
		loader.load(this.file, (model) => {	
				// Setup model initial position
				this.model = model.scene;

				this.model.updateWorldMatrix(true, true);
				this.bbox = new THREE.Box3().setFromObject(this.model);

				// Save bbox before any model transformation
				// this.scene.add( new THREE.Box3Helper(this.bbox, 0x00ff00) );

				this.model.position.x = this.settings.model.position.x;
				this.model.position.y = this.settings.model.position.y;
				this.model.position.z = this.settings.model.position.z;
				this.model.rotation.y = THREE.MathUtils.degToRad(this.settings.model.rotation);
				this.model.scale.set(this.settings.model.scale, this.settings.model.scale, this.settings.model.scale);
				
				// @resume check https://threejs.org/docs/#OBB
				// https://discourse.threejs.org/t/accurate-obb-for-gltf-oriented-bounding-box/50942
				// this.scene.add( new THREE.Box3Helper(new THREE.Box3().setFromObject(this.model), 0xf0ff0f) );

				this.scene.add(this.model);

				// const group = new THREE.Group();
				// group.add( meshA );
				
				// this.scene.receiveShadow = true;
				
				const toBeRemoved = [];
				const products = [];

				const report = {
					products: [],
					objects: [],
					removed: [],
				}; // dev

				this.scene.traverse(object => {
					// Add cast shadows (lost in skp to glb)
					if (object.isMesh){
						object.castShadow = true;
						object.receiveShadow = true;
					}
					else if (object.isLight && object.name.indexOf('cmz-') === -1){
						// Get rid of GLB lights
						toBeRemoved.push(object);
						report.removed.push(object.name);
						return;
					}
					
					if (object.material?.normalMap){
					// if (this.slug == 'jose-garrido' && object.material){
						object.material.normalMap = null;
						// object.material.metalnessMap = null;
						// object.material.roughnessMap = null;
					}

					if (object.name == 'Elementos_Parque'){
						object.children.forEach(child => {
							// child.material.transparent = true;
							// child.material.opacity = .8;
							// child.material.castShadow = false;
							// child.material.receiveShadow = false;
							// child.material.alphaTest = 1;
						});
						
						console.log(object);
					}

					if (object.name){
						report.objects.push(object.name);
					}

					if (this.isProduct(object)){
						console.log(this.settings.model.hitboxScale - 1);
						object.scale.addScalar(this.settings.model.hitboxScale - 1);
						products.push(object);
					}
				});

				// Remove useless objects
				toBeRemoved.forEach(object => {
					this.scene.remove(object);
				});

				// Create products
				products.forEach(object => {
					// Remove prefix
					object.name = this.removeProductNamePrefix(object.name);

					const product = new Plan3D_Product(object, this);
					const id = product.id;

					this.products.push(product); // @dev (allow missmatch with db)

					report.products[id] = {
						product: this.products[this.products.length - 1],
						founded: false,
					};

					// Do not create products that are not in DB (Filter for visible products present in plan)
					if (CMZ.products.find(product => product.id == id)){
						// Match with DB data
						// this.products.push(new Plan3D_Product(object, this)); // @dev (allow missmatch with db)
						report.products[id].founded = true;
					}
					else{
						// Not found in DB
						report.products[id].founded = false;
						// console.warn('Product in plan not in DB (or not visible)', id);
					}
				});

				console.log('Plan 3D products:', report); // dev

				// Dispatch event (build product object, pre-select first item)
				document.dispatchEvent(new CustomEvent('cmzSpotPlan3DLoaded', {detail: {plan: this}}));

				// document.dispatchEvent(new CustomEvent('cmzSpotPlan3DLoaded', {detail: {
				// 	plan: this,
				// 	callback: () => {
				// 		// Render once product initial status setup
				// 		this.render();
				// 	}
				// }}));

				// Load environement
				if (LANDSCAPE){
					this.landscape.init(this);

					this.landscape.layers.forEach(layer => {
						// Add layer to new group and rotate it 180° (Tileset load it in wrong direction)
						const group = new THREE.Group();
						group.add(layer.group);
						group.rotation.y = Math.PI;

						this.scene.add(group);
						// this.scene.add(layer.group);
					});
				}

				/*
				// Add landscape
				if (this.landscape?.terrain?.group){
					this.scene.add(this.landscape.terrain.group);
				}

				// Add landscape
				if (this.landscape?.bulidings?.group){
					this.scene.add(this.landscape.bulidings.group);
				}*/

				// Render first frame
				// this.start();
				this.render();

				this.loader.parentNode.removeChild(this.loader);

				// setTimeout(() => {
					// this.stop();
				// }, 8000);

				// this.render();
			},
			(xhr) => {
				const progress = Math.floor(xhr.loaded / xhr.total * 98);
				this.loader.style.setProperty('--progress', progress);
				// console.log(progress + '% loaded');
			},
		);

		/*const initLandscape = () => {
			this.landscape.init(this);
			
			window.removeEventListener('click', initLandscape);
				if (this.landscape?.terrain?.group){
					this.scene.add(this.landscape.terrain.group);
				}
		}

		window.addEventListener('click', initLandscape);*/
	}

	getCursorPosition(e){
		return {
			x: (e.clientX / window.innerWidth) * 2 - 1,
			y: -(e.clientY / window.innerHeight) * 2 + 1
		};
	}

	getSize(){
		return {width: window.innerWidth, height: window.innerHeight};
	}

	getLerpSettings(key){
		return this.settings.lerp[key] || this.settings.lerp.default;
	}

	lerp(current, target, {speed, threshold}){
		// Note: Native lerp also exist: Three.MathUtils.lerp()
		if (speed >= 1){
			return target;
		}

		// speed = speed * 100 * this.clock.getDelta();

		let distToGo = target - current;
		let delta = current + (distToGo * speed);

		if (Math.abs(distToGo) < threshold){
			// console.warn( distToGo );
			return target;
		}

		return delta;
	}

	getCurrentView(){
		return new View({
			angle: {
				x: this.controls.getAzimuthalAngle(),
				y: this.controls.getPolarAngle() - Math.PI/2,
			},
			target: {
				x: this.controls.target.x,
				y: this.controls.target.y,
				z: this.controls.target.z,
			},
			// camera: this.camera.position.copy(),
			distance: this.controls.getDistance(),
			zoom: this.camera.zoom,
			sun: {
				x: this.sun.position.x,
				y: this.sun.position.y,
				z: this.sun.position.z,
			},
			sky: {
				x: this.sky.settings.azimuth,
				y: this.sky.settings.elevation,
				exposure: this.sky.settings.exposure,
			},
			hemisphere: {
				color: this.hemisphere.color.getHex(),
			}
		});
	}

	setView(data){
		// Set new target
		this.views.target = this.getCurrentView();
		this.views.target.set(data);

		// Start smooth transition
		this.update.view = true;
		this.update.camera = true;
		this.update.compass = true;
		this.autostopIsOn = true;
		
		this.start();
		// this.startViewTransition();
	}

	updateView(){
		// Compute lerped values
		let values = {};

		Object.keys(this.views.target.data).forEach(key => {
			if (typeof this.views.target.data[key] === 'object'){
				// Nested objects
				values[key] = {};

				Object.keys(this.views.target.data[key]).forEach(n => {
					values[key][n] = this.lerp(this.views.current.data[key][n], this.views.target.data[key][n], this.getLerpSettings(key));
				});
			}/*
			else if (0&& key == 'color'){ 
				// @resume lerp color to update hemisphere light color when sun moves
				// https://threejs.org/docs/#Color.lerp
				// https://threejs.org/docs/#Color.lerpColors

				// animations have to be rewrite so it has a fixed duration
				// Try Three.MathUtils.lerp() and Color.lerpColors()

				const color = THREE.Color( 0xff0000 );
				console.log( 'Hi' );
			}*/
			else{
				// Regular variable
				values[key] = this.lerp(this.views.current.data[key], this.views.target.data[key], this.getLerpSettings(key));
			}
		});

		// Go back to start if a complete rotation was made (> 180° || < -180°)
		if (values.angle.x > Math.PI){
			values.angle.x -= Math.PI * 2;
			this.views.target.data.angle.x -= Math.PI * 2;
		}
		else if (values.angle.x < -Math.PI){
			values.angle.x += Math.PI * 2;
			this.views.target.data.angle.x += Math.PI * 2;
		}
			
		/**
		 * Set values
		 */

		// Horizontal orbit
		this.controls.minAzimuthAngle = values.angle.x;
		this.controls.maxAzimuthAngle = this.controls.minAzimuthAngle;

		// Vertical orbit
		this.controls.minPolarAngle = values.angle.y + Math.PI/2;
		this.controls.maxPolarAngle = THREE.MathUtils.degToRad(this.controls.minPolarAngle);

		// Camera position
		this.controls.target.set(values.target.x, values.target.y, values.target.z);

		if (ORTO){
			this.camera.zoom = values.zoom;
		}
		else{
			// console.log( values.zoom );
			this.controls.minDistance = values.distance;
			this.controls.maxDistance = this.controls.minDistance;
			// this.camera.position.setScalar(values.zoom);
		}

		// Lights
		this.sun.position.set(values.sun.x, values.sun.y, values.sun.z);
		// this.spotlight.position.set(values.sun.x, values.sun.y, values.sun.z);

		// console.log( values.hemisphere.color );
		// this.hemisphere.color.set(values.hemisphere.color);

		// Sky
		this.sky.settings.azimuth = values.sky.x;
		this.sky.settings.elevation = values.sky.y;
		this.sky.settings.exposure = values.sky.exposure;

		this.sky.update(); // compute and set values based on elevation and azimute
	}

	isProduct(object){
		// !isNaN(parseFloat(n)) && isFinite(n)
		return object.name.indexOf(this.settings.productName) != -1;
	}

	removeProductNamePrefix(name){
		return name.replace(this.settings.productName, ''); // Trim product prefix
		// return name;
	}

	getProduct(id){
		return this.products.find(product => product.id == id);
	}
/*
	setInteraction(name, product){
		// Bail early if no change
		if (product == this.interactions[name]){
			return;
		}

		// Reset previous
		if (this.interactions[name]){
			this.interactions[name].setState(name, false);
		}

		// Set state
		if (product){
			product.setState(name, true);
		}

		// Update global
		this.interactions[name] = product;
		this.update.highlight = true;
	}
*/
	afterInteractionChanged(){ // not used anymore
		this.update.highlight = true;
	}

	getIntersectedProduct(){
	 	// opti: https://discourse.threejs.org/t/ideas-why-r116-may-cause-outline-pass-to-affect-performance/15059/20
		this.raycaster.setFromCamera(this.cursor.current, this.camera);

		// const intersects = this.raycaster.intersectObjects( rays, false );
		const intersects = this.raycaster.intersectObject( this.scene, true );

		if (intersects.length){
			// console.log( 'Hovering', intersects[0].object.parent.name );
			console.log(intersects[0].object.name);
			return this.getProduct(intersects[0].object.name);
			// return this.getProduct(intersects[0].object.parent.name);
		}
	}

	/*updateHighlight(){
		this.outlinePass.selectedObjects = [];

		if (this.interactions.hover){
			this.outlinePass.selectedObjects.push(this.interactions.hover.object);
			return;
		}

		if (this.interactions.open){
			this.outlinePass.selectedObjects.push(this.interactions.open.object);
			return;
		}

		if (this.interactions.focus){
			this.outlinePass.selectedObjects.push(this.interactions.focus.object);
			return;
		}
	}*/

	moveCameraTo(product){
		if (!product) return;

		if (!this.settings.pan) return; // No possible if pan disabled @todo move vertically

		// Lock camera
		// this.controls.enablePan = false; // @temp

		// Upade scene matrix world (useful?)
		this.scene.updateMatrixWorld(true);

		// Get object center from bbox
		const position = new THREE.Box3().setFromObject(product.object).getCenter(new THREE.Vector3());

		const view = {
			target: {
				x: position.x,
				// y: position.y, // @uncomment @todo: move camera also on the Y axis (usefull for multistories bulidings)
				y: 0,  // @comment @todo: move camera also on the Y axis (usefull for multistories bulidings)
				z: position.z,
			}
		}

		if (this.slug == 'jose-garrido'){
			const angle = THREE.MathUtils.radToDeg(this.controls.getAzimuthalAngle());
			
			const productsOnlyFacingGarrido = ['D-1B','D-1C','D-2B','D-3B'];
			const productsOnlyFacingEstevez = ['I-1B','I-2B','I-1C','D-2A','D-1A',];

			const fov = 25;

			const cameraIsViewingGarrido = angle > (0 + fov) && angle <= (180 - fov);
			const cameraIsViewingEstevez = angle > (-180 + fov) && angle <= (0 - fov);
			
			if (productsOnlyFacingGarrido.includes(product.id) && !cameraIsViewingGarrido){
				view.angle = {
					x: THREE.MathUtils.degToRad(90) // Face Garrido
				};
			}
			else if ((productsOnlyFacingEstevez.includes(product.id) && !cameraIsViewingEstevez)){
				view.angle = {
					x: THREE.MathUtils.degToRad(-90) // Face Estevez
				};
			}
		}

		this.setView(view);
	}

	onPointerMove(e){
		if (e.isPrimary === false) return;

		// Update cursor position
		const {x, y} = this.getCursorPosition(e);
		this.cursor.current.set(x, y);

		// Get hovered product
		const product = this.getIntersectedProduct();
		
		// this.outlinePass.selectedObjects = [];
			
		// Remove status tag on mobile when view moved
		if (window.innerWidth < 1024){
			this.updateProductStateTag(null);
		}
		
		// Update hightlight if different
		if (window.innerWidth >= 1024){
			// Update status tag
			this.updateProductStateTag(product, e.clientX, e.clientY);

			if (this.productHovered != product){
				this.productHovered = product;
				// this.setInteraction('hover', product);
				
				document.dispatchEvent(new CustomEvent('cmzSpotPlan3DHoverChanged', {detail: {productId: product?.id}}))
			}
		}
	}

	updateProductStateTag(product, x = 0, y = 0){
		if (product?.planProduct && !product.planProduct.states.available){
			// Update cursor position if tag visible
			this.planView.style.setProperty('--cursor-x', x + 'px');
			this.planView.style.setProperty('--cursor-y', y + 'px');

			this.planView.setAttribute('data-hover-state', product.planProduct.status);
			this.planView.setAttribute('data-hover-display-state', product.planProduct.status_label);
			this.planView.classList.add('has-hover');
		}
		else{
			this.planView.classList.remove('has-hover');
		}
	}

	// afterProductClosed(){ // Trigger by parent
	// 	// Render
	// 	this.render();
	// }
	/*
	afterProductOpened(planProduct){
		if (!planProduct?.product3D) return;

		// Focus
		this.moveCameraTo(planProduct.product3D);

		// Render
		this.render();
	}

	afterProductFocused(planProduct){
		if (!planProduct?.product3D) return;

		// Focus
		this.moveCameraTo(planProduct.product3D);
		
		// Render
		this.render();
	}*/

	// afterProductUnfocused(){
	// 	// Render
	// 	this.render();
	// }
	/*
	closeProduct(){
		// Highlight off
		this.setInteraction('open', null);

		// Close details
		// product.closeDetails();  // inherited from Plan_Product
		document.querySelector('#cmz-spot-plan-product-none').click();

		// Render
		this.render();

	}

	openProduct(product){
		// Visual feedback
		this.setInteraction('open', product);

		// Open popup
		product.openDetails(); // inherited from Plan_Product

		console.log(product);

		// Focus
		this.moveCameraTo(product);

		// Render
		this.render();
	}

	focusProduct(product){
		// Visual feedback
		this.setInteraction('focus', product);

		if (!product){
			return;
		}

		this.moveCameraTo(product);

		// Scroll minicard slider
		document.dispatchEvent(new CustomEvent('cmzPlanProductFocused', {detail: {product}}));

		this.render();
	}
	unfocus(){
		console.log( 'unfocus' );
		this.setInteraction('focus', null);

		this.render();
	}
	*/

	onClick(e){
		const product = this.getIntersectedProduct();

		this.updateProductStateTag(product, e.clientX, e.clientY);

		if (!product){
			return;
		}

		if (!product.planProduct?.states.available){
			return;
		}

		// Update highlight
		// if (window.innerWidth >= 1024){
			// product.states.open ? this.closeProduct() : this.openProduct(product);
		// }
		// else{
			// this.focusProduct(product);
		// }

		// Scale up scope to then cascade down simultaniously to 2D and 3D Plans
		document.dispatchEvent(new CustomEvent('cmzPlanProductClicked', {detail: {productId: product.id}}));

		// Highlight
		// this.highlight(product);
	}
	/*
	onProductClicked(productId){ // trigger by Spot() via cmzPlanProductClicked in customizer.js
		const product = this.getProduct(productId);

		if (product){
			if (window.innerWidth >= 1024){
				product.states.open ? this.closeProduct() : this.openProduct(product);
			}
			else{
				this.focusProduct(product);
			}

			// product.onClick(); // inherited from Plan_Product
		}
	}
	*/

	onWindowResize(){
		const {width, height} = this.getSize();

		if (ORTO){
			this.camera.left = width / -2;
			this.camera.right = width / 2;
			this.camera.top = height / 2;
			this.camera.bottom = height / -2;
		}
		else{
			this.camera.aspect = width / height;
		}

		this.camera.updateProjectionMatrix();

		this.renderer.setSize(width, height);
		// this.composer.setSize(width, height);

		this.render();
	}

	start(){
		if (this.running) return;

		this.running = true;
		// this.clock.start();
		this.renderer.setAnimationLoop(this.render.bind(this));
	}
	
	stop(){
		if (!this.running) return;
		
		this.running = false;
		// this.clock.stop();
		this.renderer.setAnimationLoop(null);
	}
	
	requestRender(){
		// Render with debounce
		clearTimeout(this.renderTimeout);
		this.renderTimeout = setTimeout(this.render.bind(this), 10);
	}

	render(){
		// console.log( 'render' );
		
		if (DEV){
			this.stats.update();
			this.debug.update(this);
		}

		if (this.update?.view){
			this.updateView(); // Note: current view is changed but this.views.current not updated yet (updated after render)
		};

		if (this.update?.highlight){
			// this.updateHighlight();
			this.update.highlight = false; // One frame is enough
		}

		if (this.update?.compass){
			this.tools.compass.style.setProperty('--rotation', THREE.MathUtils.radToDeg(this.controls.getAzimuthalAngle()) + 'deg');
		}
		
		if (this.update?.camera){
			this.camera.updateProjectionMatrix();
		}
		
		if (this.update?.landscape){
			this.landscape.update(this.camera, this.renderer);
		}

		if (this.update?.debug){

		}

		// syncFogWithSky(this.renderer, this.sky.object, this.scene.fog);

		this.controls.update();

		// this.composer.render();
		this.renderer.render(this.scene, this.camera);

		// Update current view
		this.views.current = this.getCurrentView();
		
		// Autostop
		if (!this.autostopIsOn){
			return;
		}

		if (!this.views.previous.isDifferentFrom(this.views.current)){
			this.autostopIsOn = false;
			console.warn( 'stop' );
			
			// Release view transition controls
			if (this.update?.view){
				this.controls.minAzimuthAngle = -Infinity;
				this.controls.maxAzimuthAngle = Infinity;
				this.controls.minPolarAngle = THREE.MathUtils.degToRad(this.settings.minPolarAngle);
				this.controls.maxPolarAngle = THREE.MathUtils.degToRad(this.settings.maxPolarAngle);
				this.controls.minDistance = this.settings.minDistance; // @persp cam
				this.controls.maxDistance = this.settings.maxDistance; // @persp cam
				
				this.update.view = false;
				this.update.camera = false;
				this.update.compass = false;
			}

			// Stop loop
			this.stop();
		}

		// Backup current view
		this.views.previous = this.views.current.copy();
	}
}

// new Plan(document.querySelector(".cmz-spot"));
// export { Plan };